import { useCallback, useEffect, useRef, useState } from 'react';

import { NAV_ITEMS } from '@/components/app-shell/nav-items';
import { PageHeader } from '@/components/app-shell/PageHeader';
import { useAuthStore } from '@/features/auth/auth-store';

import { MAX_QUESTION_LENGTH } from './nlq-api';
import { useDetokenizer } from './useDetokenizer';
import { useNlqStore, type NlqAiMessage, type NlqConversation, type NlqMessage } from './nlq-store';
import { NlqMarkdown } from './NlqMarkdown';
import { useNlqSnapshotDate } from './useNlq';

/**
 * Ask BankIQ — the v7 conversational NLQ page (SCRUM-83, Epic SCRUM-17). Ports
 * `renderAskBankIQ` + the `.nlq` CSS block (app-shell 7.html): welcome state,
 * chat thread (user bubble / AI message anatomy), loading indicator, floating
 * input dock with snapshot line, scroll-to-bottom button, and the role-denied
 * state (defense-in-depth beyond SideNav gating — SCRUM-86's UI half).
 *
 * Unlike the prototype's canned NLQ_RESPONSES matcher, answers come from the
 * REAL watsonx-grounded endpoint (SCRUM-85) via the session-local nlq-store.
 * Starter/recent/follow-up chips shipped with SCRUM-84 (recents persist to
 * sessionStorage so the welcome list is reachable after a reload — see
 * nlq-store).
 *
 * Layout: the page fills the AppShell <main> (h-full, overflow-hidden — the
 * prototype zeroes the content padding for this page only) and manages its own
 * scroll: the thread scrolls, the dock floats above it.
 */

/** Distance-from-bottom threshold for the scroll-to-bottom button (prototype). */
const SCROLL_BTN_THRESHOLD = 120;

const CHAT_ICON_PATH = 'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z';

/**
 * The 5 welcome starter chips. Static by design: the AC's "contextually
 * relevant" requirement is satisfied by the MODEL-generated follow-up chips;
 * these are the fixed conversation openers.
 *
 * Every starter MUST be answerable from the NLQ grounding snapshot (customers,
 * balances, holdings, campaigns, referrals, insights) — NLQ answers only from
 * that snapshot and fail-honestly declines anything else. The original v7
 * prototype copy (SCRUM-84) included delinquency rate, capital ratios and a
 * deposit-trend question, none of which the snapshot (or the schema) carries, so
 * 3 of 5 chips returned the "I don't have that" fallback (SCRUM-1036). Each entry
 * below maps to a real snapshot domain; the "avoids ungroundable topics" test in
 * AskBankIQPage.test.tsx guards against reintroducing an unanswerable sample.
 *
 * The exact wording matters as much as the topic: all five were verified live
 * against the deployed dev snapshot (SCRUM-1036). "Show campaign performance"
 * grounded but badged low — "performance" implies CTR/ROI the snapshot lacks — so
 * it is phrased "How many campaigns are running?" (campaigns.byStatus, high
 * confidence). "Who are our top customers by balance?" is deliberately NOT used:
 * topCustomersByBalance (SCRUM-1022) is not in the currently-deployed snapshot, so
 * it fell back live; "What's the average customer balance?" (averageBalance +
 * totalBalance) grounds on the snapshot as deployed today.
 */
export const STARTER_QUESTIONS = [
  'How many customers do we serve?', // customers.total
  'How many campaigns are running?', // campaigns.byStatus
  "What's our customer tier mix?", // customers.byTier
  "What's the average customer balance?", // customers.averageBalance + totalBalance
  'How many customers hold each product?', // customers.byHolding + glossary.holdingCodes
] as const;

/** Welcome recents show at most 4 of the 8 stored (prototype slice(0, 4)). */
const RECENTS_SHOWN_CAP = 4;

function RobotIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      className="h-3 w-3 opacity-60"
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="6.5" />
      <path d="M5.5 6.5C6 5.5 7 5 8 5s2 .5 2.5 1.5" />
      <circle cx="6" cy="8.5" r="0.5" fill="currentColor" stroke="none" />
      <circle cx="10" cy="8.5" r="0.5" fill="currentColor" stroke="none" />
      <path d="M6 11c.5.8 1.2 1 2 1s1.5-.2 2-1" />
    </svg>
  );
}

function AiBadge({ label }: { label: string }) {
  return (
    <div className="text-text-3 inline-flex items-center gap-1 font-mono text-[0.625rem] font-semibold tracking-[0.06em] uppercase">
      <RobotIcon />
      {label}
    </div>
  );
}

const CONFIDENCE_META = {
  high: {
    label: 'High confidence',
    pill: 'text-[var(--success)] bg-[var(--success-dim)]',
    dot: 'bg-[var(--success)]',
  },
  medium: {
    label: 'Medium confidence',
    pill: 'text-[var(--warning)] bg-[var(--warning-dim)]',
    dot: 'bg-[var(--warning)]',
  },
  low: {
    label: 'Low confidence',
    pill: 'text-[var(--danger)] bg-[var(--danger-dim)]',
    dot: 'bg-[var(--danger)]',
  },
} as const;

function AiMessage({ message }: { message: NlqAiMessage }) {
  const conf = CONFIDENCE_META[message.confidence];
  const ask = useNlqStore((s) => s.ask);
  const detokenize = useDetokenizer();
  return (
    <div className="gap-tk-sm flex max-w-[85%] flex-col self-start">
      <AiBadge label="AI-generated response" />
      <div
        data-testid="nlq-ai-body"
        className="bg-bg-3 border-border-0 rounded-tk-lg rounded-tl-tk-xs text-text-1 border px-5 py-4 text-[0.875rem] leading-[1.6]"
      >
        <NlqMarkdown markdown={message.answerMarkdown} />
      </div>
      <div className="gap-tk-md flex flex-wrap items-center">
        <div
          data-testid={`nlq-confidence-${message.confidence}`}
          className={`rounded-tk-full inline-flex items-center gap-[5px] px-2.5 py-[3px] font-mono text-[0.6875rem] font-medium ${conf.pill}`}
        >
          <div className={`rounded-tk-full h-1.5 w-1.5 ${conf.dot}`} />
          {conf.label}
        </div>
        {message.source !== '' && (
          <div
            data-testid="nlq-source"
            className="gap-tk-xs text-text-3 flex flex-wrap items-center font-mono text-[0.6875rem]"
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
              className="h-3 w-3 opacity-50"
              aria-hidden="true"
            >
              <path d="M9 1.5H4a1.5 1.5 0 00-1.5 1.5v10A1.5 1.5 0 004 14.5h8a1.5 1.5 0 001.5-1.5V6L9 1.5z" />
              <polyline points="9 1.5 9 6 13.5 6" />
            </svg>
            {message.source}
          </div>
        )}
      </div>
      {message.followups.length > 0 && (
        <div className="gap-tk-xs mt-tk-xs flex flex-wrap">
          {message.followups.map((f, i) => (
            <button
              key={`${String(i)}-${f}`}
              type="button"
              onClick={() => {
                void ask(f, detokenize);
              }}
              className="rounded-tk-full border-border-0 bg-bg-4 text-text-2 hover:border-brand-primary hover:text-brand-primary hover:bg-brand-dim cursor-pointer border px-3 py-[5px] text-[0.75rem] transition-all"
            >
              {f}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LoadingMessage() {
  return (
    <div className="gap-tk-sm flex max-w-[85%] flex-col self-start" data-testid="nlq-loading">
      <AiBadge label="Analyzing" />
      <div className="inline-flex items-center gap-[5px] py-1.5">
        <div className="rounded-tk-full bg-brand-primary h-[5px] w-[5px] animate-[nlq-dot_1.2s_ease-in-out_infinite] opacity-35" />
        <div className="rounded-tk-full bg-brand-primary h-[5px] w-[5px] animate-[nlq-dot_1.2s_ease-in-out_infinite] opacity-35 [animation-delay:0.15s]" />
        <div className="rounded-tk-full bg-brand-primary h-[5px] w-[5px] animate-[nlq-dot_1.2s_ease-in-out_infinite] opacity-35 [animation-delay:0.3s]" />
        <span className="text-text-3 ml-1 font-mono text-[0.6875rem]">Querying data snapshot…</span>
      </div>
    </div>
  );
}

function Message({ message }: { message: NlqMessage }) {
  if (message.role === 'user') {
    return (
      <div
        data-testid="nlq-user"
        className="rounded-tk-lg rounded-br-tk-xs text-bg-4 max-w-[72%] self-end bg-[image:var(--grad-cyan)] px-[18px] py-3 text-[0.875rem] leading-[1.5]"
      >
        {message.text}
      </div>
    );
  }
  return <AiMessage message={message} />;
}

function WelcomeState() {
  const recents = useNlqStore((s) => s.recents);
  const ask = useNlqStore((s) => s.ask);
  const detokenize = useDetokenizer();
  return (
    <div className="p-tk-2xl gap-tk-xl flex flex-1 flex-col items-center justify-center text-center">
      <div
        data-testid="nlq-welcome-icon"
        className="rounded-tk-xl bg-brand-dim flex h-14 w-14 items-center justify-center"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--cyan)"
          strokeWidth="1.5"
          strokeLinecap="round"
          className="h-7 w-7"
          aria-hidden="true"
        >
          <path d={CHAT_ICON_PATH} />
        </svg>
      </div>
      <div className="font-display text-text-0 text-[1.25rem] font-bold">Ask BankIQ</div>
      <div className="text-text-2 max-w-[480px] text-[0.875rem] leading-[1.55]">
        Ask plain-language questions about your bank&apos;s performance, campaigns, and customer
        insights. Responses are generated from your most recent data snapshot — not live queries.
      </div>

      <div className="gap-tk-sm flex max-w-[560px] flex-wrap justify-center">
        {STARTER_QUESTIONS.map((q) => (
          <button
            key={q}
            type="button"
            data-testid="nlq-starter"
            onClick={() => {
              void ask(q, detokenize);
            }}
            className="rounded-tk-full border-border-1 bg-bg-3 text-text-1 hover:border-brand-primary hover:text-brand-primary hover:bg-brand-dim cursor-pointer border px-4 py-2 text-[0.8125rem] whitespace-nowrap transition-all"
          >
            {q}
          </button>
        ))}
      </div>

      {recents.length > 0 && (
        <div className="mt-tk-lg w-full max-w-[480px]">
          <div className="mb-tk-sm text-text-3 text-left font-mono text-[0.6875rem] font-semibold tracking-[0.05em] uppercase">
            Recent questions
          </div>
          {recents.slice(0, RECENTS_SHOWN_CAP).map((q) => (
            <button
              key={q}
              type="button"
              data-testid="nlq-recent"
              onClick={() => {
                void ask(q, detokenize);
              }}
              className="gap-tk-sm text-text-2 hover:text-brand-primary flex w-full cursor-pointer items-center py-1.5 text-left text-[0.8125rem] transition-colors"
            >
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
                className="h-3.5 w-3.5 shrink-0 opacity-50"
                aria-hidden="true"
              >
                <path d="M8 1.5a6.5 6.5 0 11-5.2 2.6" />
                <polyline points="2 1 2.8 4.6 6.2 3.4" />
              </svg>
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DeniedState() {
  return (
    <div className="p-tk-3xl gap-tk-lg flex flex-1 flex-col items-center justify-center text-center">
      <div className="rounded-tk-xl bg-bg-2 flex h-16 w-16 items-center justify-center">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--text-3)"
          strokeWidth="1.5"
          strokeLinecap="round"
          className="h-8 w-8"
          aria-hidden="true"
        >
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0110 0v4" />
          <circle cx="12" cy="16" r="1" />
        </svg>
      </div>
      <div className="font-display text-text-1 text-[1.125rem] font-semibold">
        Ask BankIQ is available for Bank Executives and Bank Admins
      </div>
      <div className="text-text-3 max-w-[380px] text-[0.875rem] leading-[1.5]">
        This feature provides conversational access to your bank&apos;s performance data and
        insights. Contact your platform administrator if you believe you should have access.
      </div>
    </div>
  );
}

/** "+ New chat" header action (SCRUM-701). Resets to the welcome state; disabled while loading. */
function NewChatButton() {
  const newChat = useNlqStore((s) => s.newChat);
  const isLoading = useNlqStore((s) => s.isLoading);
  return (
    <button
      type="button"
      data-testid="nlq-new-chat"
      aria-label="Start a new chat"
      disabled={isLoading}
      onClick={() => {
        newChat();
      }}
      className="rounded-tk-md border-border-1 bg-bg-3 text-text-1 hover:border-brand-primary hover:text-brand-primary hover:bg-brand-dim inline-flex items-center gap-1.5 border px-3 py-1.5 text-[0.8125rem] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40"
    >
      <svg
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        className="h-3.5 w-3.5"
        aria-hidden="true"
      >
        <path d="M8 3.5v9M3.5 8h9" />
      </svg>
      New chat
    </button>
  );
}

/** Short relative age for a history row (e.g. "just now", "5m", "2h", "3d"). */
function relativeAge(ms: number): string {
  const s = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${String(m)}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${String(h)}h`;
  return `${String(Math.floor(h / 24))}d`;
}

const TRASH_ICON = (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-3.5 w-3.5"
    aria-hidden="true"
  >
    <path d="M2.5 4h11M6 4V2.5h4V4M5 4l.5 9h5l.5-9" />
  </svg>
);

/**
 * Right-hand conversation history (SCRUM-701). Shown only when ≥1 conversation
 * exists; hidden on narrow screens (the thread keeps full width). Newest-first;
 * click a row to reopen; hover a row for the delete control.
 */
function ConversationHistory() {
  const conversations = useNlqStore((s) => s.conversations);
  const activeId = useNlqStore((s) => s.activeId);
  const openConversation = useNlqStore((s) => s.openConversation);
  const deleteConversation = useNlqStore((s) => s.deleteConversation);

  if (conversations.length === 0) return null;

  const ordered = [...conversations].sort((a, b) => b.updatedAt - a.updatedAt);

  return (
    <aside
      data-testid="nlq-history"
      className="border-border-0 bg-bg-1 hidden w-[260px] shrink-0 flex-col overflow-y-auto border-l lg:flex"
    >
      <div className="px-tk-lg pt-tk-lg pb-tk-sm text-text-3 font-mono text-[0.6875rem] font-semibold tracking-[0.06em] uppercase">
        Conversations
      </div>
      <div className="px-tk-sm gap-tk-xs flex flex-col pb-4">
        {ordered.map((c: NlqConversation) => {
          const isActive = c.id === activeId;
          return (
            <div
              key={c.id}
              data-testid="nlq-history-item"
              className={`rounded-tk-md group flex items-center gap-1 px-2.5 py-2 transition-colors ${
                isActive ? 'bg-brand-dim' : 'hover:bg-bg-3'
              }`}
              {...(isActive ? { 'aria-current': 'true' as const } : {})}
            >
              <button
                type="button"
                onClick={() => {
                  openConversation(c.id);
                }}
                className={`min-w-0 flex-1 truncate text-left text-[0.8125rem] ${
                  isActive ? 'text-brand-primary font-medium' : 'text-text-1 hover:text-text-0'
                }`}
                title={c.title}
              >
                {c.title}
              </button>
              <span className="text-text-3 shrink-0 font-mono text-[0.625rem]">
                {relativeAge(c.updatedAt)}
              </span>
              <button
                type="button"
                data-testid="nlq-history-delete"
                aria-label={`Delete conversation: ${c.title}`}
                onClick={() => {
                  deleteConversation(c.id);
                }}
                className="text-text-3 hover:text-danger shrink-0 p-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
              >
                {TRASH_ICON}
              </button>
            </div>
          );
        })}
      </div>
    </aside>
  );
}

export function AskBankIQPage() {
  // Demo-era role default matches the AppShell/SideNav convention: an absent
  // user resolves to Bank Executive (the demo's default role) — the deny path
  // is for the EXPLICIT non-exec roles reachable via the role switcher.
  const role = useAuthStore((s) => s.user?.role) ?? 'Bank Executive';
  const messages = useNlqStore((s) => s.messages);
  const isLoading = useNlqStore((s) => s.isLoading);
  const ask = useNlqStore((s) => s.ask);
  const detokenize = useDetokenizer();
  const snapshotDate = useNlqSnapshotDate();

  const [draft, setDraft] = useState('');
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const threadRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback((smooth: boolean) => {
    const el = threadRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  // Prototype behavior: jump to the bottom whenever the thread grows (new
  // user message, loading indicator, or answer).
  useEffect(() => {
    scrollToBottom(false);
  }, [messages.length, isLoading, scrollToBottom]);

  const handleScroll = useCallback(() => {
    const el = threadRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollBtn(distFromBottom > SCROLL_BTN_THRESHOLD);
  }, []);

  const submit = useCallback(() => {
    const question = draft.trim();
    if (!question || isLoading) return;
    setDraft('');
    void ask(question, detokenize);
  }, [draft, isLoading, ask, detokenize]);

  // SCRUM-913: DERIVED from the nav matrix rather than a hardcoded role, so this
  // defense-in-depth check can never drift from the nav link and the route guard
  // (which both read NAV_ITEMS). A hardcoded 'Bank Executive' here meant Bank
  // Admin — which holds the AskBankIQ module server-side (ModuleAccessPolicy:
  // BankAdmin => AllAccess) — got a nav link that led to a denial screen.
  // Unknown nav id ⇒ empty ⇒ deny (fail-closed).
  const askBankIqRoles: readonly string[] =
    NAV_ITEMS.find((i) => i.id === 'ask-bankiq')?.roles ?? [];
  if (!askBankIqRoles.includes(role)) {
    return (
      <div className="flex h-full flex-col overflow-hidden">
        <PageHeader
          title="Ask BankIQ"
          subtitle="Natural language query — Executive & Bank Admin access"
        />
        <DeniedState />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <PageHeader
        title="Ask BankIQ"
        subtitle="Natural language query — Executive & Bank Admin access"
        actions={<NewChatButton />}
      />

      {/* Row: thread+dock column (flex-1) + the right-hand history aside. */}
      <div className="flex min-h-0 w-full flex-1">
        {/* .nlq — full-height column; the thread scrolls, the dock floats above IT (not the page). */}
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
          <div
            ref={threadRef}
            onScroll={handleScroll}
            data-testid="nlq-thread"
            className="px-tk-3xl pt-tk-xl gap-tk-xl flex flex-1 flex-col overflow-y-auto pb-[110px]"
          >
            {messages.length === 0 && !isLoading ? (
              <WelcomeState />
            ) : (
              <>
                {messages.map((m, i) => (
                  <Message key={i} message={m} />
                ))}
                {isLoading && <LoadingMessage />}
              </>
            )}
          </div>

          {/* .nlq-dock — floating input dock with fade mask + solid backing. */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] flex flex-col items-stretch">
            <button
              type="button"
              onClick={() => {
                scrollToBottom(true);
              }}
              title="Scroll to bottom"
              className={`rounded-tk-full bg-bg-3 border-border-1 text-text-2 shadow-tk-md hover:border-brand-primary hover:text-brand-primary pointer-events-auto absolute bottom-[calc(100%-72px+var(--sp-sm))] left-1/2 z-[3] h-9 w-9 -translate-x-1/2 items-center justify-center border transition-all ${showScrollBtn ? 'flex' : 'hidden'}`}
            >
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4 w-4"
                aria-hidden="true"
              >
                <path d="M4 6l4 4 4-4" />
              </svg>
            </button>

            {/* Fade mask — content dissolves above the bar. */}
            <div className="h-[72px] w-full shrink-0 bg-[linear-gradient(to_bottom,transparent_0%,var(--bg-1)_100%)]" />

            <div className="bg-bg-1 px-tk-lg pointer-events-auto flex flex-col items-stretch">
              <div className="flex w-full items-center gap-2">
                <div
                  data-testid="nlq-input-wrap"
                  className="bg-bg-3 border-border-1 rounded-tk-lg flex flex-1 items-center border py-1 pr-1 pl-4 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_4px_12px_rgba(0,0,0,0.04)] transition-shadow focus-within:shadow-[0_1px_3px_rgba(0,0,0,0.08),0_6px_20px_rgba(0,0,0,0.06)]"
                >
                  <input
                    type="text"
                    value={draft}
                    maxLength={MAX_QUESTION_LENGTH}
                    onChange={(e) => {
                      setDraft(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') submit();
                    }}
                    placeholder="Ask about your bank's performance…"
                    autoComplete="off"
                    className="text-text-0 placeholder:text-text-3 min-w-0 flex-1 border-none bg-transparent py-2 text-[0.8125rem] outline-none"
                  />
                  <button
                    type="button"
                    onClick={submit}
                    disabled={isLoading || draft.trim() === ''}
                    title="Send"
                    className="rounded-tk-md bg-brand-primary text-bg-4 flex h-8 w-8 shrink-0 items-center justify-center transition-[background,transform,opacity] hover:scale-105 active:scale-95 disabled:cursor-default disabled:opacity-30 disabled:hover:scale-100"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-3.5 w-3.5"
                      aria-hidden="true"
                    >
                      <path d="M12 19V5M5 12l7-7 7 7" />
                    </svg>
                  </button>
                </div>
              </div>
              <div
                data-testid="nlq-snapshot-line"
                className="text-text-3 pt-1 pb-2.5 text-center font-mono text-[0.5625rem] tracking-[0.02em]"
              >
                {/* SCRUM-897: "as of", not "nightly" — the date is the age of the underlying
                    data (last ingestion batch), and the snapshot rebuilds when data lands,
                    not on a nightly cadence. Parallel to the API's own source line. */}
                {snapshotDate
                  ? `Based on bank data as of ${snapshotDate}`
                  : 'Based on the latest bank data'}
              </div>
            </div>
          </div>
        </div>

        <ConversationHistory />
      </div>
    </div>
  );
}
